import { ChangeDetectorRef, Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ChildComponent } from 'src/app/components/child/child.component';
import { AppliedTermsService } from 'src/app/services/modules/instant-rate-manager/applied-terms.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { SummernoteDirective } from 'src/app/directives/summernote.directive';

interface AppliedTerm {
  id: number;
  name: string;
  description: string;
}

@Component({
  selector: 'app-applied-terms',
  templateUrl: './applied-terms.component.html',
  styleUrls: ['./applied-terms.component.scss'],
})
export class AppliedTermsComponent extends ChildComponent implements OnInit {
  @ViewChild('termModalTemplate') termModalTemplate!: TemplateRef<any>;
  @ViewChildren(SummernoteDirective) summernoteInstances!: QueryList<SummernoteDirective>;

  private modalRef: any;

  terms: AppliedTerm[] = [];
  selectedTerm: AppliedTerm | null = null;
  isCreatingNew = false;
  isListLoading = false;
  isSaving = false;
  isSubmitted = false;
  paginationInfo: any = null;
  search = '';
  termSearch$ = new Subject<string>();
  sortBy = '-updated_at';
  sortOptions = [
    { label: 'Latest Update', value: '-updated_at' },
    { label: 'Oldest Update', value: 'updated_at' },
    { label: 'Name (A-Z)', value: 'name' },
    { label: 'Name (Z-A)', value: '-name' },
    { label: 'Newest Created', value: '-created_at' },
    { label: 'Oldest Created', value: 'created_at' },
  ];
  editorHeight = 300;
  defaultHeight = 300;

  constructor(
    private appliedTermsService: AppliedTermsService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getAppliedTerms();
    this.termSearch$.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      this.cancelEdit();
      this.getAppliedTerms(1);
    });
  }

  getAppliedTerms(page: number = 1) {
    this.isListLoading = true;

    const payload = {
      page,
      page_size: 10,
      name: this.search || undefined,
      ordering: this.sortBy,
    };

    this.appliedTermsService.getAppliedTerms(payload).subscribe({
      next: (res: any) => {
        const response = res.data;
        this.paginationInfo = {
          count: response.count,
          current_page: response.current_page,
          total_pages: response.total_pages,
          next: response.next,
          previous: response.previous,
        };
        this.terms = response.results || response.result || [];
        if (this.selectedTerm && !this.isCreatingNew) {
          const refreshed = this.terms.find(t => t.id === this.selectedTerm!.id);
          if (refreshed) this.selectedTerm = { ...refreshed };
        }
        this.isListLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isListLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  openCreateModal() {
    this.isCreatingNew = true;
    this.isSubmitted = false;
    this.selectedTerm = { id: 0, name: '', description: '' };
    this.modalRef = this.modalService.open(this.termModalTemplate, { centered: true, size: 'lg', backdrop: 'static', keyboard: false });
    this.modalRef.result.then(() => { }, () => this.cancelEdit());
  }

  selectTerm(term: AppliedTerm) {
    this.isCreatingNew = false;
    this.isSubmitted = false;
    this.selectedTerm = { ...term };

    this.resetEditorDimensions();
    this.cdr.detectChanges();
  }

  async saveTerm() {
    if (!this.selectedTerm) return;

    this.isSubmitted = true;
    if (!this.selectedTerm.name?.trim()) return;
    if (this.isDescriptionEmpty(this.selectedTerm.description)) return;

    const payload = {
      name: this.selectedTerm.name,
      description: this.selectedTerm.description,
    };

    try {
      this.isSaving = true;

      if (this.isCreatingNew) {
        // 1. Await the creation (Service returns a Promise)
        const res: any = await this.appliedTermsService.createAppliedTerm(payload);
        const newTerm = res.data;

        // 2. Add to local list (top of the list)
        this.terms = [newTerm, ...this.terms];

        // 3. Update count badge
        if (this.paginationInfo) this.paginationInfo.count++;

        this.modalRef?.close();
        AlertUtil.success('Term created successfully!');
      } else {
        // 1. Await the update
        const res: any = await this.appliedTermsService.updateAppliedTerm(this.selectedTerm.id, payload);
        const updatedTerm = res.data;

        // 2. Find and update object in the local array
        const index = this.terms.findIndex(t => t.id === this.selectedTerm!.id);
        if (index !== -1) {
          this.terms[index] = { ...updatedTerm };
        }

        AlertUtil.success('Term updated successfully!');
      }

      // Reset UI State
      this.cancelEdit();
      this.resetEditorDimensions();

    } catch (err: any) {
      AlertUtil.error(err?.error?.message || err?.message || 'An unexpected error occurred.');
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  async deleteTerm(id: number, event: Event) {
    event.stopPropagation();

    const result = await Swal.fire({
      title: 'Delete Term?',
      html: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        container: 'swal-on-top',
        confirmButton: 'btn btn-secondary text-white mx-2',
        cancelButton: 'btn btn-outline-primary mx-2',
        actions: 'd-flex justify-content-end gap-2',
      },
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          await this.appliedTermsService.deleteAppliedTerm(id);
          return true;
        } catch (err: any) {
          Swal.showValidationMessage(err?.error?.message || 'Failed to delete term.');
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      AlertUtil.success('Term deleted successfully!');
      this.cancelEdit();
      this.getAppliedTerms(this.paginationInfo?.current_page || 1);
    }
  }

  isDescriptionEmpty(description: string): boolean {
    if (!description) return true;
    const div = document.createElement('div');
    div.innerHTML = description;
    return !div.textContent?.trim();
  }

  cancelEdit() {
    this.selectedTerm = null;
    this.isCreatingNew = false;
    this.isSubmitted = false;

    this.resetEditorDimensions();
    this.cdr.detectChanges();
  }

  private resetEditorDimensions() {
    // Give Angular a tick to ensure the DOM elements exist
    setTimeout(() => {
      this.summernoteInstances.forEach(instance => {
        instance.resetHeight(300); // Or whatever your default is
      });
    }, 50);
  }

  onSortChange() {
    this.cancelEdit();
    this.getAppliedTerms(1);
  }

  goToPage(page: number) {
    this.cancelEdit();
    this.getAppliedTerms(page);
  }

  onPageChange(direction: 'next' | 'prev') {
    if (!this.paginationInfo) return;
    const targetPage = direction === 'next'
      ? this.paginationInfo.current_page + 1
      : this.paginationInfo.current_page - 1;
    if (targetPage >= 1 && targetPage <= this.paginationInfo.total_pages) {
      this.goToPage(targetPage);
    }
  }


}
